import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, MessageCircle, Share2, Bookmark, VolumeX, Volume2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useSaveDish } from '@/hooks/useSaveDish';
import { trackEvent } from '@/services/analyticsService';
import CommentSheet from '@/components/comments/CommentSheet';
import { getOptimizedAvatarUrl } from '@/utils/imageUtils';
import { StoryPost } from '@/hooks/useDishStoryPosts';
import { shareDish } from '@/utils/shareUtils';

interface StoryMediaOverlayProps {
  post: StoryPost;
  isPaused: boolean;
  onPause: () => void;
  onResume: () => void;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

const StoryMediaOverlay: React.FC<StoryMediaOverlayProps> = ({
  post,
  isPaused,
  onPause,
  onResume,
  isMuted,
  onToggleMute,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [localLiked, setLocalLiked] = useState(post.user_has_liked || false);
  const [localLikesCount, setLocalLikesCount] = useState(post.likes_count || 0);
  const [localShareCount, setLocalShareCount] = useState(post.dishes?.share_count || 0);
  const [localSaveCount, setLocalSaveCount] = useState(post.dishes?.save_count || 0);
  const [isVisible, setIsVisible] = useState(true);
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);

  const { isSaved, toggleSave, isToggling } = useSaveDish(post.dish_id || '');

  // Sync optimistic state when post changes
  useEffect(() => {
    setLocalLiked(post.user_has_liked || false);
    setLocalLikesCount(post.likes_count || 0);
    setLocalShareCount(post.dishes?.share_count || 0);
    setLocalSaveCount(post.dishes?.save_count || 0);
    setIsVisible(true);
  }, [post.id]);

  // Pause/resume based on visibility
  useEffect(() => {
    if (isVisible) {
      onPause();
    } else {
      onResume();
    }
  }, [isVisible, onPause, onResume]);

  // Auto-hide after 3s
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => setIsVisible(false), 3000);
    return () => clearTimeout(timer);
  }, [isVisible]);

  const handleTap = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  // Like mutation
  const likeMutation = useMutation({
    mutationFn: async ({ isLiked }: { isLiked: boolean }) => {
      if (isLiked) {
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user!.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('likes')
          .insert({ post_id: post.id, user_id: user!.id });
        if (error) throw error;
      }
    },
    onMutate: async ({ isLiked }) => {
      setLocalLiked(!isLiked);
      setLocalLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    },
    onError: (_, { isLiked }) => {
      setLocalLiked(isLiked);
      setLocalLikesCount(prev => isLiked ? prev + 1 : prev - 1);
      toast.error('Failed to update like');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dish-story-posts', post.dish_id] });
    },
  });

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { toast.info('Please sign in to like!'); return; }
    trackEvent('Post Engagement', 'Like', post.id);
    likeMutation.mutate({ isLiked: localLiked });
  };

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCommentSheetOpen(true);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!post.dish_id) { toast.info('No dish linked to this post'); return; }
    trackEvent('Dish Engagement', 'Share', post.dishes?.name || 'Unknown');
    await shareDish({
      name: post.dishes?.name || 'Check out this dish!',
      slug: post.dishes?.slug,
      id: post.dish_id,
      restaurant: post.dishes?.restaurants?.name,
    });
    setLocalShareCount(prev => prev + 1);
    await supabase.rpc('increment_share_count', { dish_id: post.dish_id });
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { toast.info('Please sign in to save dishes!'); return; }
    if (!post.dish_id) { toast.info('No dish linked to this post'); return; }
    trackEvent('Dish Engagement', 'Save', post.dishes?.name || 'Unknown');
    setLocalSaveCount(prev => isSaved ? Math.max(prev - 1, 0) : prev + 1);
    toggleSave();
  };

  return (
    <>
      {/* Full-screen tap target — pause on hold, resume on release */}
      <div
        className="absolute inset-0 z-0 pointer-events-auto"
        onClick={handleTap}
        onPointerDown={(e) => { e.stopPropagation(); onPause(); }}
        onPointerUp={(e) => { e.stopPropagation(); onResume(); }}
        onPointerCancel={(e) => { e.stopPropagation(); onResume(); }}
      />

      {/* Main overlay content */}
      <div
        className={`absolute inset-0 z-20 pointer-events-none flex flex-col justify-between transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Top gradient — sits below progress bar */}
        <div className="h-28 bg-gradient-to-b from-black/60 to-transparent" />

        {/* Bottom section */}
        <div
          className="bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-20 pb-4 px-4"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
        >
          <div className="flex items-end justify-between gap-4">
            {/* Left: user + dish info */}
            <div className="flex-1 min-w-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (post.profiles?.id) navigate(`/profile/${post.profiles.id}`);
                }}
                className="flex items-center gap-2 mb-2 pointer-events-auto hover:opacity-80 transition-opacity"
              >
                <Avatar className="h-8 w-8 border border-white/30">
                  <AvatarImage src={getOptimizedAvatarUrl(post.profiles?.avatar_url)} />
                  <AvatarFallback className="bg-white/20 text-white text-xs">
                    {post.profiles?.username?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-white font-semibold text-sm">
                  @{post.profiles?.username || 'user'}
                </span>
              </button>

              {post.dishes?.name && (
                <p className="text-white font-bold text-base truncate">{post.dishes.name}</p>
              )}
              {post.dishes?.restaurants?.name && (
                <p className="text-white/70 text-sm truncate">📍 {post.dishes.restaurants.name}</p>
              )}
              {post.content && (
                <p className="text-white/90 text-sm mt-2 line-clamp-2">{post.content}</p>
              )}
            </div>

            {/* Right: action buttons */}
            <div className={`flex flex-col items-center gap-4 ${isVisible ? 'pointer-events-auto' : 'pointer-events-none'}`}>
              {/* Volume (video only) */}
              {onToggleMute !== undefined && (
                <button onClick={(e) => { e.stopPropagation(); onToggleMute?.(); }} className="flex flex-col items-center text-white">
                  <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                  </div>
                </button>
              )}

              {/* Like */}
              <button onClick={handleLike} className="flex flex-col items-center text-white">
                <div className={`w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${localLiked ? 'bg-[#cfef00]/20' : 'bg-white/10'}`}>
                  <ThumbsUp className={`w-6 h-6 transition-colors ${localLiked ? 'text-[#cfef00] fill-[#cfef00]' : 'text-white'}`} />
                </div>
                <span className="text-xs mt-1">{localLikesCount}</span>
              </button>

              {/* Comment */}
              <button onClick={handleComment} className="flex flex-col items-center text-white">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <span className="text-xs mt-1">{post.comment_count || 0}</span>
              </button>

              {/* Save */}
              {post.dish_id && (
                <button onClick={handleSave} disabled={isToggling} className="flex flex-col items-center text-white">
                  <div className={`w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${isSaved ? 'bg-[#cfef00]/20' : 'bg-white/10'}`}>
                    <Bookmark className={`w-6 h-6 transition-colors ${isSaved ? 'text-[#cfef00] fill-[#cfef00]' : 'text-white'}`} />
                  </div>
                  <span className={`text-xs mt-1 ${localSaveCount === 0 ? 'invisible' : ''}`}>{localSaveCount} Save</span>
                </button>
              )}

              {/* Share */}
              <button onClick={handleShare} className="flex flex-col items-center text-white">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <Share2 className="w-6 h-6" />
                </div>
                <span className={`text-xs mt-1 ${localShareCount === 0 ? 'invisible' : ''}`}>{localShareCount} Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Comment Sheet */}
      {isCommentSheetOpen && (
        <CommentSheet
          postId={post.id}
          dishName={post.dishes?.name}
          onClose={() => setIsCommentSheetOpen(false)}
        />
      )}
    </>
  );
};

export default StoryMediaOverlay;
